import React, { PureComponent } from 'react';
import { reduxForm, change } from 'redux-form';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { gtmEventPush } from 'utils/gtm/events';
import isServer from 'utils/isServer';
import getClassifiedsSite from 'utils/getClassifiedsSite';

// Helpers
import classNames from 'classnames';

// Components
import BasicFilters from '../BasicFilters';
import AdvancedFilters from '../AdvancedFilters';
import LeasingAdvancedFilters from '../LeasingAdvancedFilters';

import CarTypes from '../CarTypes';
import Icon from 'Common/Icon';

// Styles
import bootstrap from 'vendor/bootstrap/_bootstrap_custom.scss';
import styles from 'components/SearchForm.scss';
import PartnershipLogo from '../../Common/PartnershipLogo';

const formInitialised = R.compose(R.not, R.isEmpty);
const fieldsHaveChanged = R.compose(R.not, R.equals);
const isFormInitialisedAndFormChanged = (oldProps, newProps) => {
  return fieldsHaveChanged(oldProps, newProps) && formInitialised(oldProps);
};

/**
 * <SearchForm /> component.
 * @return {class} new searchform instance
 */
class SearchForm extends PureComponent<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.resetField = this.resetField.bind(this);
    this.postcodeRef = React.createRef();
    this.state = {
      highlight: false
    };
  }

  /* eslint-disable camelcase */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (isFormInitialisedAndFormChanged(this.props.fields, nextProps.fields)) {
      const currentPropsWithoutPostCode = R.omit(
        ['sortBy', 'isPostCodeValid', 'postCode', 'lat', 'long'],
        this.props.fields
      );
      const nextPropsWithoutPostCode = R.omit(
        ['sortBy', 'isPostCodeValid', 'postCode', 'lat', 'long'],
        nextProps.fields
      );
      if (this.props.fields.postCode && !nextProps.fields.postCode) {
        this.resetField('withinDistance');
      } else if (
        fieldsHaveChanged(currentPropsWithoutPostCode, nextPropsWithoutPostCode)
      ) {
        this.props.getResults();
      }
    }
  }

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField(field) {
    this.props.dispatch(change('search', field, null));

    if (field === 'make') {
      this.props.dispatch(change('search', 'model', null));
    }

    if (field === 'withinDistance' || field === 'isPostCodeValid') {
      this.props.dispatch(change('search', 'lat', null));
      this.props.dispatch(change('search', 'long', null));
    }
  }

  /**
   * scrollToPostCodeRef
   * @param {object} e - event
   * @return {void}
   */
  scrollToPostCodeRef = e => {
    const { isPostcodeValid } = this.props;

    gtmEventPush({
      method: 'filterFormSearchButtonClick',
      args: {
        isPostcodeValid
      }
    });

    if (!isPostcodeValid) {
      e.preventDefault();
      window.scrollTo(0, this.postcodeRef.offsetTop);
      this.setState({ highlight: true });
    }
  };

  /**
   * setPostcodeRef
   * @param {object} el - element
   * @return {void}
   */
  setPostcodeRef = el => {
    this.postcodeRef = el;
  };

  /**
   * Renders SearchForm.
   *
   * @return {JSX} rendered SearchForm.
   */
  render(): Element<*> | null {
    const {
      handleSubmit,
      checkPostcode,
      resultsLength,
      toggleFurtherOptions,
      showFurtherOptions,
      makes,
      models,
      additonalOptions,
      fields,
      resetAllFields,
      isPostcodeValid,
      priceFrom,
      priceTo,
      isResultsLoading,
      isUILoading,
      isMobile,
      postcodeVisited,
      thisPageData,
      isLeasingPage,
      isStockCarsPage
    } = this.props;

    const disableSearchButton =
      !isLeasingPage &&
      fields &&
      fields.postCode &&
      fields.postCode.length > 0 &&
      !isPostcodeValid;

    const topRowClasses = showFurtherOptions
      ? classNames(styles.formRow, styles.basicFieldsRow, styles.bottomBorder)
      : classNames(styles.formRow, styles.basicFieldsRow);

    const optionsClass = showFurtherOptions
      ? classNames(styles.filterButtonContainer, styles.lessOptions)
      : styles.filterButtonContainer;

    const classifiedsSite = getClassifiedsSite();
    const siteText = classifiedsSite === 'autocar' ? 'Autocar' : 'What Car?';
    const friendlyName = thisPageData.friendlyName
      ? thisPageData.friendlyName.replace('cars', '')
      : '';

    const isLeasing = thisPageData.type === 'leasing';
    const isInStock = thisPageData.type === 'in stock cars';
    const carPrefix = isInStock ? 'new' : 'used';
    const carSufix = isInStock ? `, ${friendlyName} right now` : ' for sale';
    const desktopOrMobileText = isMobile ? '-mobile' : '-desktop';
    const formId = `${thisPageData.searchFormName}${desktopOrMobileText}`;

    return (
      <form
        onSubmit={handleSubmit}
        className={classNames(
          styles.searchForm,
          bootstrap.formInline,
          bootstrap.clearfix
        )}
        name={thisPageData.searchFormName}
        id={formId}
      >
        <div className={styles.heading}>
          <h1>
            {isLeasing
              ? `Leasing deals on ${siteText}`
              : `Find your perfect ${carPrefix} car${carSufix}`}
          </h1>
          <PartnershipLogo />
        </div>

        <div className={styles.searchFormContainer}>
          <div className={styles.formGroup}>
            <BasicFilters
              topRowClasses={topRowClasses}
              styles={styles}
              bootstrap={bootstrap}
              makes={makes}
              models={models}
              checkPostcode={!isLeasingPage ? checkPostcode : undefined}
              fields={fields}
              isPostcodeValid={!isLeasingPage ? isPostcodeValid : undefined}
              priceFrom={priceFrom}
              priceTo={priceTo}
              resetField={this.resetField}
              isResultsLoading={isResultsLoading}
              isUILoading={isUILoading}
              setPostcodeRef={!isLeasingPage ? this.setPostcodeRef : undefined}
              highlight={
                !isLeasingPage && (postcodeVisited || this.state.highlight)
              }
              resultsLength={resultsLength}
              disableSearchButton={disableSearchButton}
              scrollToPostCodeRef={
                !isLeasingPage ? this.scrollToPostCodeRef : undefined
              }
              isMobile={isMobile}
              isLeasingPage={isLeasingPage}
              isStockCarsPage={isStockCarsPage}
            />

            <div className={classNames(styles.actionButtonsRow)}>
              <div className={optionsClass}>
                <button
                  className={classNames(
                    bootstrap.btn,
                    bootstrap.btnLink,
                    bootstrap.btnLg,
                    styles.moreOptionsButton
                  )}
                  type="button"
                  onClick={toggleFurtherOptions}
                >
                  <Icon
                    iconName="more-options"
                    size={isMobile ? 'medium' : 'default'}
                    className={styles.iconFilter}
                  />
                  {showFurtherOptions ? 'Less options' : 'More options'}
                  {!isMobile && (
                    <Icon
                      iconName={
                        showFurtherOptions
                          ? 'chevron-up-small'
                          : 'chevron-down-small'
                      }
                      size={'default'}
                      className={classNames(
                        styles.iconFilter,
                        styles.arrowIcon
                      )}
                    />
                  )}
                </button>
              </div>
              <button
                id="clear-all-btn"
                className={styles.clearBtn}
                disabled={R.keys(fields).length < 1}
                onClick={e => {
                  e.preventDefault();
                  gtmEventPush({
                    method: 'filterForm',
                    args: {
                      itemLabel: 'Clear all'
                    }
                  });
                  resetAllFields();
                }}
              >
                Clear all
              </button>
            </div>

            {showFurtherOptions && (
              <div>
                <div className={styles.filterLabel}>Body types</div>
                <CarTypes
                  bootstrap={bootstrap}
                  styles={styles}
                  additonalOptions={additonalOptions}
                  fields={fields}
                  isLoading={isResultsLoading}
                />
                {isLeasingPage ? (
                  <LeasingAdvancedFilters
                    styles={styles}
                    bootstrap={bootstrap}
                    makes={makes}
                    models={models}
                    additonalOptions={additonalOptions}
                    fields={fields}
                    resetField={this.resetField}
                    isLoading={isResultsLoading}
                    resultsLength={resultsLength}
                    isMobile={isMobile}
                  />
                ) : (
                  <AdvancedFilters
                    styles={styles}
                    bootstrap={bootstrap}
                    makes={makes}
                    models={models}
                    additonalOptions={additonalOptions}
                    fields={fields}
                    resetField={this.resetField}
                    isLoading={isResultsLoading}
                    resultsLength={resultsLength}
                    isMobile={isMobile}
                    isStockCarsPage={isStockCarsPage}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    );
  }
}

// eslint-disable-next-line no-class-assign
SearchForm = reduxForm({
  form: 'search',
  destroyOnUnmount: false
})(SearchForm);

export default connect((state, props) => {
  // initial values will be set only once and it should'nt be on server side as it is setting from states.
  if (!isServer()) {
    const initialValues = {
      postCode: !props.isLeasingPage ? state.ui.lastPostCode : null,
      isPostCodeValid: !props.isLeasingPage
        ? Boolean(state.ui.lastPostCode)
        : null
    };

    return {
      initialValues
    };
  }
})(SearchForm);

export { SearchForm };



// WEBPACK FOOTER //
// ./src/Common/SearchForm/index.js